import { LevelPhase } from "@highmarch/domain";
import type { LevelSim } from "@highmarch/domain";
import { groundAt } from "../render/terrain";
import { icon } from "../ui/iconsKit";
import { rareIndexOf } from "../rare/rareApi";
import { isWyrm } from "../rare/wyrmApi";
import { WYRM_COPY } from "../ui/wyrmModel";

/** The screen band the map is seen in (CSS px): between the HUD and whatever covers the bottom. */
export interface MarkerBand { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number }

/** Where an edge marker sits for a target at screen point `p`: on the band's edge toward it, and which way it points. */
export interface EdgeMark { readonly x: number; readonly y: number; readonly angle: number }

/**
 * An off-screen target's marker (QoL C3): null while `p` is inside the band (less `inset`); otherwise the point where
 * the line from the band's centre to `p` leaves the inset band, and the angle (radians, 0 = right, clockwise on
 * screen) the marker's arrow points along. A target far off to one side and one just past a corner both read right.
 */
export function edgeMark(p: { x: number; y: number }, band: MarkerBand, inset: number): EdgeMark | null {
  const l = band.left + inset, r = band.right - inset, t = band.top + inset, b = band.bottom - inset;
  if (r <= l || b <= t) return null;
  if (p.x >= l && p.x <= r && p.y >= t && p.y <= b) return null;
  const cx = (l + r) / 2, cy = (t + b) / 2, dx = p.x - cx, dy = p.y - cy;
  // the largest k ≤ 1 that keeps centre + k·d inside the inset band
  const kx = dx === 0 ? Infinity : (dx > 0 ? r - cx : l - cx) / dx;
  const ky = dy === 0 ? Infinity : (dy > 0 ? b - cy : t - cy) / dy;
  const k = Math.min(1, kx, ky);
  return { x: cx + dx * k, y: cy + dy * k, angle: Math.atan2(dy, dx) };
}

/**
 * `m` moved off `avoid` (a control over the map, the thumb cluster) by the shortest slide that keeps it inside the
 * inset band: a marker under the whole-map button could be neither seen nor tapped. `pad` is the marker's reach from
 * its centre (plate and arrow). The angle stays: it still points from the band's centre toward the target.
 */
export function clearOf(m: EdgeMark, avoid: MarkerBand | null, pad: number, band: MarkerBand, inset: number): EdgeMark {
  if (avoid === null) return m;
  const l = avoid.left - pad, r = avoid.right + pad, t = avoid.top - pad, b = avoid.bottom + pad;
  if (m.x <= l || m.x >= r || m.y <= t || m.y >= b) return m;
  const bl = band.left + inset, br = band.right - inset, bt = band.top + inset, bb = band.bottom - inset;
  // Slide along the edge the marker sits on (a marker on the right edge moves up or down it, not into the map).
  const EDGE_EPS = 0.5;
  const onSide = Math.abs(m.x - bl) < EDGE_EPS || Math.abs(m.x - br) < EDGE_EPS, onTopBottom = Math.abs(m.y - bt) < EDGE_EPS || Math.abs(m.y - bb) < EDGE_EPS;
  const across = onTopBottom || !onSide, along = onSide || !onTopBottom;
  const candidates = [...(across ? [{ x: l, y: m.y }, { x: r, y: m.y }] : []), ...(along ? [{ x: m.x, y: t }, { x: m.x, y: b }] : [])];
  let best: { x: number; y: number } | null = null, bestD = Infinity;
  for (const c of candidates) {
    if (c.x < bl || c.x > br || c.y < bt || c.y > bb) continue;
    const d = Math.abs(c.x - m.x) + Math.abs(c.y - m.y);
    if (d < bestD) { bestD = d; best = c; }
  }
  return best === null ? m : { x: best.x, y: best.y, angle: m.angle };
}

/** `m` clear of every rectangle in `avoid` (a second pass: sliding off one control can land a marker on another). */
export function clearOfAll(m: EdgeMark, avoid: readonly MarkerBand[], band: MarkerBand): EdgeMark {
  let out = m;
  for (let pass = 0; pass < 2; pass++) for (const a of avoid) out = clearOf(out, a, MARKERS.ClearPx, band, MARKERS.InsetPx);
  return out;
}

/** A thing the player may want to know about when it is off screen. */
export type MarkerKind = "portal" | "boss" | "base" | "rare";

/** What each marker shows: its glyph and colour class. */
const MARKER_ICON: Record<MarkerKind, string> = { portal: "target", boss: "skull", base: "heart", rare: "star" };

/** Marker tuning: kept off the very edge, a fingertip wide, and only the nearest threats to the fortress count. */
export const MARKERS = {
  /** Distance (CSS px) from the band's edge to a marker's centre. */
  InsetPx: 26,
  /** How far a marker reaches from its centre (the 36 px plate's radius, its arrow, a gap): kept this far off a control. */
  ClearPx: 30,
  /** An enemy this many road cells (or fewer) from the fortress is a threat to it. */
  BaseThreatCells: 4,
  /** A marker moves only when its spot changes by at least this (px) or its angle by this (radians): no per-frame writes. */
  MovePx: 0.5,
  TurnRad: 0.01,
} as const;

/** One marker's target in world units (x, z: the ground), or null when there is none to mark now. */
export interface MarkerTargets {
  portal: { x: number; z: number } | null; boss: { x: number; z: number } | null; base: { x: number; z: number } | null; rare: { x: number; z: number } | null;
  /** M40: the star marker is the Starfall Wyrm's (its prismatic look). */
  legend?: boolean;
}

/** Where the portal, the first living boss and a threatened fortress are (null for each that is not there to mark). */
export function markerTargets(sim: LevelSim): MarkerTargets {
  const over = sim.isOver;
  const p = sim.road.portal;
  const portal = !over && sim.phase !== LevelPhase.OpeningDraft ? { x: p.x, z: p.y } : null;
  let boss: { x: number; z: number } | null = null, rare: { x: number; z: number } | null = null, threat = false, legend = false;
  const toBase = sim.road.length.toNumber();
  for (const e of sim.enemySlots) {
    if (!e.alive) continue;
    // M36: the rare has its own gold marker (it is drawn as a boss, but the boss marker stays for a real boss)
    // M40: the Starfall Wyrm takes the star marker too (they never share the field)
    if (rare === null && (rareIndexOf(sim, e) > 0 || isWyrm(sim, e))) { rare = { x: e.x.toNumber(), z: e.y.toNumber() }; legend = isWyrm(sim, e); }
    else if (boss === null && e.isBoss) boss = { x: e.x.toNumber(), z: e.y.toNumber() };
    if (toBase - e.distance.toNumber() <= MARKERS.BaseThreatCells) threat = true;
  }
  const b = sim.grid.base;
  return { portal, boss: over ? null : boss, base: !over && threat ? { x: b.x, z: b.y } : null, rare: over ? null : rare, legend };
}

/**
 * Edge markers for the portal, a boss and a threatened fortress while they are off screen (QoL C3). A marker is a
 * small plate with the target's glyph and an arrow pointing at it; a tap on it glides the view there. Written only
 * when a marker moves, shows or hides.
 */
export class OffscreenMarkers {
  readonly root: HTMLDivElement;
  private readonly els = new Map<MarkerKind, { el: HTMLButtonElement; arrow: HTMLElement; x: number; y: number; a: number; on: boolean }>();

  constructor(parent: HTMLElement, onTap: (kind: MarkerKind) => void) {
    this.root = document.createElement("div");
    this.root.className = "edge-markers";
    for (const kind of ["portal", "boss", "base", "rare"] as const) {
      const el = document.createElement("button");
      el.className = `edge-mark ${kind} hidden`;
      el.setAttribute("aria-label", kind === "portal" ? "Show the portal" : kind === "boss" ? "Show the boss" : kind === "rare" ? "Show the rare" : "Show the fortress");
      el.innerHTML = `<i class="arrow"></i><span class="glyph">${icon(MARKER_ICON[kind], 16)}</span>`;
      el.addEventListener("click", () => onTap(kind));
      this.root.append(el);
      // ±Infinity, not NaN, for "never placed": the first comparison must say it moved
      this.els.set(kind, { el, arrow: el.querySelector<HTMLElement>(".arrow")!, x: -Infinity, y: -Infinity, a: -Infinity, on: false });
    }
    parent.append(this.root);
  }

  /**
   * Places each marker for its target (world), projected by `project`, against the visible `band`, clear of `avoid`
   * (the HUD's controls over the map: the thumb cluster on whichever side the left-handed setting puts it, pause and
   * speed while they ride on the card tray).
   */
  update(targets: MarkerTargets, project: (x: number, y: number, z: number) => { x: number; y: number }, band: MarkerBand, avoid: readonly MarkerBand[] = []): void {
    for (const [kind, m] of this.els) {
      const w = targets[kind];
      const edge = w ? edgeMark(project(w.x, groundAt(w.x, w.z), w.z), band, MARKERS.InsetPx) : null;
      const mark = edge === null ? null : clearOfAll(edge, avoid, band);
      if ((mark !== null) !== m.on) { m.on = mark !== null; m.el.classList.toggle("hidden", !m.on); }
      if (mark === null) continue;
      if (kind === "rare" && m.el.classList.contains("legend") !== !!targets.legend) {
        m.el.classList.toggle("legend", !!targets.legend);
        m.el.setAttribute("aria-label", targets.legend ? WYRM_COPY.markerLabel : "Show the rare");
      }
      if (Math.abs(mark.x - m.x) >= MARKERS.MovePx || Math.abs(mark.y - m.y) >= MARKERS.MovePx) {
        m.x = mark.x; m.y = mark.y;
        m.el.style.transform = `translate(${mark.x.toFixed(1)}px, ${mark.y.toFixed(1)}px)`;
      }
      if (!(Math.abs(mark.angle - m.a) < MARKERS.TurnRad)) { m.a = mark.angle; m.arrow.style.transform = `rotate(${mark.angle.toFixed(3)}rad)`; }
    }
  }

  hideAll(): void { for (const m of this.els.values()) if (m.on) { m.on = false; m.el.classList.add("hidden"); } }

  dispose(): void { this.root.remove(); }
}
